import { put, call, select, takeLatest } from "redux-saga/effects";
import { stopSubmit } from "redux-form";
import * as Api from "./usersAPI";
import * as UserService from "./UserService";
import { USER_FORM_NAME } from "../../../../components/AdminPanelComponent/Users/constants";
import {
  fetchUsersListRequest,
  fetchUsersListSuccess,
  fetchUsersListError,
  fetchSupervisorsListRequest,
  fetchSupervisorsListSuccess,
  fetchSupervisorsListError,
  blockUserRequest,
  blockUserSuccess,
  blockUserError,
  unblockUserRequest,
  unblockUserSuccess,
  unblockUserError,
  resetUserPasswordRequest,
  resetUserPasswordSuccess,
  resetUserPasswordError,
  createUserRequest,
  createUserSuccess,
  createUserError,
  updateUserRequest,
  updateUserSuccess,
  updateUserError,
  checkUsernameAvailabilityRequest,
  checkUsernameAvailabilitySuccess,
  checkUsernameAvailabilityError,
  fetchCurrentUserRequest,
  fetchCurrentUserSuccess,
  fetchCurrentUserError,
  fetchTemplatesRequest,
  fetchTemplatesSuccess,
  fetchTemplatesError,
  fetchUserDetailRequest,
  fetchUserDetailSuccess,
  fetchUserDetailError,
  fetchWorkloadWorkkardsRequest,
  fetchWorkloadWorkkardsSuccess,
  fetchWorkloadWorkkardsError,
  fetchWorkloadActivitiesRequest,
  fetchWorkloadActivitiesSuccess,
  fetchWorkloadActivitiesError,
  transferWorkloadRequest,
  transferWorkloadSuccess,
  transferWorkloadError,
} from "./usersSlice";


function* buildCurrentFetchParams() {
  const filters = yield select(
    (state) => state.adminPanel.usersData.filters,
  );
  const { searchTerm, activeFilter } = filters;
  const params = {};

  if (searchTerm && searchTerm.trim()) {
    params.search_columns = "email,first_name,last_name,title";
    params.search_string = searchTerm.trim();
  } else {
    switch (activeFilter) {
      case "Active":
        params.deleted = false;
        break;
      case "Blocked":
        params.deleted = true;
        break;
      case "Temporary":
        params.temp_user = true;
        break;
      case "All":
      default:
        break;
    }
  }

  return params;
}

function* fetchUsersListSaga(action) {
  try {
    const params = action.payload || {};
    const isSearch = !!(params.search_string && params.search_string.trim());
    const response = isSearch
      ? yield call(Api.searchUsers, params)
      : yield call(Api.fetchUsersList, params);

    if (!response) {
      console.error("fetchUsersListSaga: no response (request may have failed)");
      yield put(fetchUsersListError("Request failed — please try again"));
      return;
    }

    if (UserService.isSuccessResponse(response)) {
      const normalized = UserService.normalizeUserListResponse(response);
      yield put(fetchUsersListSuccess(normalized));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to fetch users");
      yield put(fetchUsersListError(errorMsg));
    }
  } catch (error) {
    console.error("Fetch users list error:", error);
    yield put(
      fetchUsersListError(UserService.getErrorMessage(error, "Failed to fetch users")),
    );
  }
}

function* fetchSupervisorsListSaga(action) {
  try {
    const { page = 1, limit = 100, search_string } = action.payload || {};
    const trimmedSearch = (search_string || "").trim();
    const isSearch = trimmedSearch.length > 0;

    const params = isSearch
      ? {
          page_no: page,
          page_size: limit,
          search_columns: "email,first_name,last_name,title",
          search_string: trimmedSearch,
        }
      : { page, limit };

    const response = isSearch
      ? yield call(Api.searchUsers, params)
      : yield call(Api.fetchUsersList, params);

    if (!response) {
      console.error("fetchSupervisorsListSaga: no response");
      yield put(fetchSupervisorsListError("Request failed — please try again"));
      return;
    }

    if (UserService.isSuccessResponse(response)) {
      const normalized = UserService.normalizeUserListResponse(response);
      yield put(fetchSupervisorsListSuccess({
        users: normalized.users,
        count: normalized.count,
        page,
      }));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to fetch supervisors");
      yield put(fetchSupervisorsListError(errorMsg));
    }
  } catch (error) {
    console.error("Fetch supervisors list error:", error);
    yield put(fetchSupervisorsListError(
      UserService.getErrorMessage(error, "Failed to fetch supervisors")
    ));
  }
}

function* blockUserSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(Api.blockUser, { userId });

    if (UserService.isSuccessResponse(response)) {
      yield put(blockUserSuccess({ userId }));
      const refetchParams = yield call(buildCurrentFetchParams);
      yield put(fetchUsersListRequest(refetchParams));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to block user");
      yield put(blockUserError(errorMsg));
    }
  } catch (error) {
    console.error("Block user error:", error);
    yield put(blockUserError(UserService.getErrorMessage(error, "Failed to block user")));
  }
}

function* unblockUserSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(Api.unblockUser, { userId });

    if (UserService.isSuccessResponse(response)) {
      yield put(unblockUserSuccess({ userId }));
      const refetchParams = yield call(buildCurrentFetchParams);
      yield put(fetchUsersListRequest(refetchParams));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to unblock user");
      yield put(unblockUserError(errorMsg));
    }
  } catch (error) {
    console.error("Unblock user error:", error);
    yield put(
      unblockUserError(UserService.getErrorMessage(error, "Failed to unblock user")),
    );
  }
}

function* resetUserPasswordSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(Api.resetUserPassword, { userId });

    if (UserService.isSuccessResponse(response)) {
      yield put(resetUserPasswordSuccess({ message: response?.message || "Password reset successfully" }));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to reset password");
      yield put(resetUserPasswordError(errorMsg));
    }
  } catch (error) {
    console.error("Reset password error:", error);
    yield put(
      resetUserPasswordError(UserService.getErrorMessage(error, "Failed to reset password")),
    );
  }
}

function* createUserSaga(action) {
  try {
    const response = yield call(Api.createUser, action.payload);

    if (UserService.isSuccessResponse(response)) {
      const createdUser = UserService.normalizeUser(response.user ?? response.data ?? {});
      yield put(createUserSuccess(createdUser));
      const refetchParams = yield call(buildCurrentFetchParams);
      yield put(fetchUsersListRequest(refetchParams));
    } else {
      const { message, fieldErrors } = UserService.parseUserApiError(response, "Failed to create user");
      if (Object.keys(fieldErrors).length > 0) {
        yield put(stopSubmit(USER_FORM_NAME, { ...fieldErrors, _error: message }));
      }
      yield put(createUserError(message));
    }
  } catch (error) {
    console.error("Create user error:", error);
    const { message, fieldErrors } = UserService.parseUserApiError(error, "Failed to create user");
    if (Object.keys(fieldErrors).length > 0) {
      yield put(stopSubmit(USER_FORM_NAME, { ...fieldErrors, _error: message }));
    }
    yield put(createUserError(message));
  }
}

function* updateUserSaga(action) {
  try {
    const response = yield call(Api.updateUser, action.payload);

    if (UserService.isSuccessResponse(response)) {
      const updatedUser = UserService.normalizeUser(response.user ?? response.data ?? {});
      yield put(updateUserSuccess(updatedUser));
      const refetchParams = yield call(buildCurrentFetchParams);
      yield put(fetchUsersListRequest(refetchParams));
    } else {
      const { message, fieldErrors } = UserService.parseUserApiError(response, "Failed to update user");
      if (Object.keys(fieldErrors).length > 0) {
        yield put(stopSubmit(USER_FORM_NAME, { ...fieldErrors, _error: message }));
      }
      yield put(updateUserError(message));
    }
  } catch (error) {
    console.error("Update user error:", error);
    const { message, fieldErrors } = UserService.parseUserApiError(error, "Failed to update user");
    if (Object.keys(fieldErrors).length > 0) {
      yield put(stopSubmit(USER_FORM_NAME, { ...fieldErrors, _error: message }));
    }
    yield put(updateUserError(message));
  }
}

function* checkUsernameAvailabilitySaga(action) {
  try {
    const { username } = action.payload;
    const response = yield call(Api.checkUsernameAvailability, { username });

    const isAvailable =
      response?.status === true ||
      response?.available === true;

    const isTaken =
      response?.status === false ||
      response?.available === false;

    if (isAvailable) {
      yield put(
        checkUsernameAvailabilitySuccess({
          available: true,
          message: response.msg || response.message || "Username is available.",
        }),
      );
    } else if (isTaken) {
      yield put(
        checkUsernameAvailabilitySuccess({
          available: false,
          message: response.msg || response.message || "This username is already registered with Exzeo.",
        }),
      );
    } else {
      const errorMsg = UserService.getErrorMessage(
        response,
        "Failed to check username availability",
      );
      yield put(checkUsernameAvailabilityError(errorMsg));
    }
  } catch (error) {
    console.error("Check username availability error:", error);
    yield put(
      checkUsernameAvailabilityError(
        UserService.getErrorMessage(error, "Failed to check username availability"),
      ),
    );
  }
}

function* fetchCurrentUserSaga() {
  try {
    const response = yield call(Api.fetchCurrentUser);

    if (UserService.isSuccessResponse(response)) {
      const currentUser = UserService.normalizeCurrentUser(response);
      yield put(fetchCurrentUserSuccess(currentUser));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to fetch current user");
      yield put(fetchCurrentUserError(errorMsg));
    }
  } catch (error) {
    console.error("Fetch current user error:", error);
    yield put(
      fetchCurrentUserError(UserService.getErrorMessage(error, "Failed to fetch current user")),
    );
  }
}

function* fetchTemplatesSaga() {
  try {
    const response = yield call(Api.fetchUserTemplates);

    if (UserService.isSuccessResponse(response)) {
      const templates = UserService.normalizeTemplatesResponse(response);
      yield put(fetchTemplatesSuccess(templates));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to fetch templates");
      yield put(fetchTemplatesError(errorMsg));
    }
  } catch (error) {
    console.error("Fetch templates error:", error);
    yield put(
      fetchTemplatesError(UserService.getErrorMessage(error, "Failed to fetch templates")),
    );
  }
}

function* fetchUserDetailSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(Api.fetchUserDetail, { userId });

    if (UserService.isSuccessResponse(response)) {
      const normalizedDetail = UserService.normalizeUserDetailResponse(response);
      yield put(fetchUserDetailSuccess(normalizedDetail));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to fetch user details");
      yield put(fetchUserDetailError(errorMsg));
    }
  } catch (error) {
    console.error("Fetch user detail error:", error);
    yield put(
      fetchUserDetailError(UserService.getErrorMessage(error, "Failed to fetch user details")),
    );
  }
}

function* fetchWorkloadWorkkardsSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(Api.fetchWorkloadWorkkards, { userId });

    if (UserService.isSuccessResponse(response)) {
      const normalized = UserService.normalizeWorkloadWorkkardsResponse(response);
      yield put(fetchWorkloadWorkkardsSuccess(normalized));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to fetch workload workkards");
      yield put(fetchWorkloadWorkkardsError(errorMsg));
    }
  } catch (error) {
    console.error("Fetch workload workkards error:", error);
    yield put(
      fetchWorkloadWorkkardsError(UserService.getErrorMessage(error, "Failed to fetch workload workkards")),
    );
  }
}

function* fetchWorkloadActivitiesSaga(action) {
  try {
    const { userId } = action.payload;
    const response = yield call(Api.fetchWorkloadActivities, { userId });

    if (UserService.isSuccessResponse(response)) {
      const normalized = UserService.normalizeWorkloadActivitiesResponse(response);
      yield put(fetchWorkloadActivitiesSuccess(normalized));
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to fetch workload activities");
      yield put(fetchWorkloadActivitiesError(errorMsg));
    }
  } catch (error) {
    console.error("Fetch workload activities error:", error);
    yield put(
      fetchWorkloadActivitiesError(UserService.getErrorMessage(error, "Failed to fetch workload activities")),
    );
  }
}

function* transferWorkloadSaga(action) {
  try {
    const response = yield call(Api.transferWorkload, action.payload);

    if (UserService.isSuccessResponse(response)) {
      yield put(transferWorkloadSuccess({ message: response?.message || "Workload transferred successfully" }));
      
      const userId = action.payload?.fromUserId;
      if (userId) {
        yield put(fetchWorkloadWorkkardsRequest({ userId }));
        yield put(fetchWorkloadActivitiesRequest({ userId }));
      }
    } else {
      const errorMsg = UserService.getErrorMessage(response, "Failed to transfer workload");
      yield put(transferWorkloadError(errorMsg));
    }
  } catch (error) {
    console.error("Transfer workload error:", error);
    yield put(
      transferWorkloadError(UserService.getErrorMessage(error, "Failed to transfer workload")),
    );
  }
}

export default function* usersSaga() {
  yield takeLatest(fetchUsersListRequest.type, fetchUsersListSaga);
  yield takeLatest(fetchSupervisorsListRequest.type, fetchSupervisorsListSaga);
  yield takeLatest(blockUserRequest.type, blockUserSaga);
  yield takeLatest(unblockUserRequest.type, unblockUserSaga);
  yield takeLatest(resetUserPasswordRequest.type, resetUserPasswordSaga);
  yield takeLatest(createUserRequest.type, createUserSaga);
  yield takeLatest(updateUserRequest.type, updateUserSaga);
  yield takeLatest(checkUsernameAvailabilityRequest.type, checkUsernameAvailabilitySaga);
  yield takeLatest(fetchCurrentUserRequest.type, fetchCurrentUserSaga);
  yield takeLatest(fetchTemplatesRequest.type, fetchTemplatesSaga);
  yield takeLatest(fetchUserDetailRequest.type, fetchUserDetailSaga);
  yield takeLatest(fetchWorkloadWorkkardsRequest.type, fetchWorkloadWorkkardsSaga);
  yield takeLatest(fetchWorkloadActivitiesRequest.type, fetchWorkloadActivitiesSaga);
  yield takeLatest(transferWorkloadRequest.type, transferWorkloadSaga);
}